import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip, AlertTriangle, Copy, Link2, Unlink, Trash2, Loader2 } from 'lucide-react';

import { ingestionService, type EmailedIntakeRow } from '@/services/ingestion';
import { formatDeskDate, formatDeskDateTime, formatDeskTime } from '@/utils/dates';

/**
 * Emailed Intake — the originations mailbox as a triage table.
 *
 * Column order is the order the desk scans mail in: WHEN it arrived, WHO sent
 * it, WHAT it says, WHAT rode along. Received date/time leads because that is
 * also the sort key — the list is ordered by the email's own Date header, not
 * by when the parser happened to process it.
 *
 * Three actions the old card list could not express:
 *   Discard    drop junk/duplicates out of the pipeline (archive, never delete)
 *   Reconcile  declare several messages to be one deal
 *   Split      undo a wrong join
 */

type Filter = 'all' | 'waiting' | 'supporting' | 'junk' | 'grouped';

/** 'Waiting in intake' and 'Supporting mail' are the Intake strip's own words
 *  and the server's own sets for them — the strip's reconciliation, not a
 *  predicate of this tab's. They used to be 'Not yet a deal' and 'Duplicates',
 *  which after four re-forwards read 7 and 0 beside the strip's 5 and 5
 *  (stress test C12). */
const FILTERS: { key: Filter; label: string; countKey?: keyof EmailedCounts }[] = [
  { key: 'all', label: 'All', countKey: 'total' },
  { key: 'waiting', label: 'Waiting in intake', countKey: 'waitingInIntake' },
  { key: 'supporting', label: 'Supporting mail', countKey: 'supportingMail' },
  { key: 'junk', label: 'Junk suspected', countKey: 'junkSuspected' },
  { key: 'grouped', label: 'Grouped', countKey: 'grouped' },
];

interface EmailedCounts {
  total: number;
  supportingMail: number;
  waitingInIntake: number;
  duplicates: number;
  junkSuspected: number;
  grouped: number;
}

/** Stable per-group accent so members of one deal are scannable at a glance. */
const GROUP_ACCENTS = ['#d9b768', '#7aa2c4', '#9fbf8f', '#c48f7a', '#a892c4', '#c4b47a'];

function groupAccent(groupId: string | null): string {
  if (!groupId) return GROUP_ACCENTS[0];
  let hash = 0;
  for (let i = 0; i < groupId.length; i += 1) hash = (hash * 31 + groupId.charCodeAt(i)) >>> 0;
  return GROUP_ACCENTS[hash % GROUP_ACCENTS.length];
}

/**
 * When a message arrived, as the New York desk reads it — never in the viewer's
 * own zone. The same message used to read Aug 18 here and Aug 19 for a colleague
 * on another coast, and neither matched the Received date on the deal it became
 * (the desk day, `core.desk_calendar`). The time column is headed "Time (ET)".
 */
export function receivedParts(iso: string | null): { date: string; time: string; title: string } {
  if (!iso) return { date: '—', time: '', title: 'No date header on this message' };
  const date = formatDeskDate(iso, 'short');
  const time = formatDeskTime(iso);
  // Neither a day nor an instant: show what was stored, not a made-up date.
  if (date === String(iso).trim() && !time) return { date: '—', time: '', title: String(iso) };
  return { date, time, title: formatDeskDateTime(iso) };
}

function statusTone(row: EmailedIntakeRow): { label: string; className: string; title?: string } {
  const muted = 'text-stone-500 border-stone-600/40';
  if (row.convertedToLoanId && row.dealKilled) {
    // A killed deal is not 'Deal' in green beside the live one.
    const title = `On killed deal ${row.convertedToLoanId}`;
    return { label: row.status === 'declined' ? 'Declined' : 'Killed', className: muted, title };
  }
  if (row.convertedToLoanId) {
    return {
      label: 'Deal',
      className: 'text-emerald-300/90 border-emerald-400/25',
      title: `On deal ${row.convertedToLoanId}`,
    };
  }
  if (row.status === 'declined') return { label: 'Declined', className: muted };
  if (row.status === 'reviewed') return { label: 'Reviewed', className: 'text-amber-200/90 border-amber-400/25' };
  return { label: 'New', className: 'text-stone-300 border-stone-500/40' };
}

/** The group chip. A group is one deal — the server splits mail that became
 *  different deals — and mail on a killed deal says which killed deal. */
function groupChip(row: EmailedIntakeRow): { label: string; title: string } {
  const on = row.dealKilled && row.groupLoanId ? `killed deal ${row.groupLoanId}` : 'this deal';
  const lead = row.dealKilled ? 'Killed' : 'Deal';
  return {
    label: row.groupRole === 'primary' ? `${lead} ×${row.groupSize}` : `+${row.groupSize - 1}`,
    title: `${row.groupSize} messages on ${on} — ${row.groupRole}`,
  };
}

export default function EmailedIntakeTable({
  onOpenRecord,
  focus = null,
  onClearFocus,
}: {
  onOpenRecord?: (id: string) => void;
  /** An Intake strip figure opened on this tab: show exactly the records it
   *  counted. Selecting 'Supporting mail 1' used to open every email. */
  focus?: { label: string; ids: string[] } | null;
  onClearFocus?: () => void;
}) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<null | 'discard' | 'reconcile' | 'notADeal'>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['emailed-intake'],
    queryFn: () => ingestionService.getEmailedIntake(),
    refetchInterval: 30_000,
  });

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const counts: EmailedCounts = data?.counts ?? {
    total: 0, supportingMail: 0, waitingInIntake: 0, duplicates: 0, junkSuspected: 0, grouped: 0,
  };

  const focusIds = useMemo(() => (focus ? new Set(focus.ids) : null), [focus]);
  // Counted records this tab cannot show — an uploaded package is supporting
  // mail too — are named, never dropped silently.
  const focusNotEmailed = focusIds
    ? [...focusIds].filter((id) => !rows.some((r) => r.id === id)).length
    : 0;

  const visible = useMemo(() => {
    const scoped = focusIds ? rows.filter((r) => focusIds.has(r.id)) : rows;
    switch (filter) {
      case 'waiting': return scoped.filter((r) => r.waitingInIntake);
      case 'supporting': return scoped.filter((r) => r.supportingMail);
      case 'junk': return scoped.filter((r) => r.junkSuspected);
      case 'grouped': return scoped.filter((r) => (r.groupSize ?? 1) > 1);
      default: return scoped;
    }
  }, [rows, filter, focusIds]);

  const selectedRows = useMemo(
    () => visible.filter((r) => selected.has(r.id)),
    [visible, selected],
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['emailed-intake'] });
    queryClient.invalidateQueries({ queryKey: ['ingestion-queue'] });
  };

  const onMutationError = (err: unknown) => {
    const detail =
      (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
    setError(detail || 'The action could not be completed.');
  };

  const discard = useMutation({
    mutationFn: (ids: string[]) => ingestionService.discardEmailed(ids, 'junk'),
    onSuccess: () => { setSelected(new Set()); setError(null); setConfirming(null); invalidate(); },
    onError: onMutationError,
  });

  /** Junk that already became a deal. Removes both halves in one action —
   *  leaving either behind is the dead end this replaces: Discard refused a
   *  linked record and told the operator to kill the deal, and killing it did
   *  not make the message discardable, so the row stayed for ever. */
  const notADeal = useMutation({
    mutationFn: (ids: string[]) =>
      ingestionService.removeNotADeal(ids, 'not a financing request'),
    onSuccess: () => { setSelected(new Set()); setError(null); setConfirming(null); invalidate(); },
    onError: onMutationError,
  });

  const reconcile = useMutation({
    mutationFn: ({ primaryId, memberIds }: { primaryId: string; memberIds: string[] }) =>
      ingestionService.reconcileEmailed(primaryId, memberIds),
    onSuccess: () => { setSelected(new Set()); setError(null); setConfirming(null); invalidate(); },
    onError: onMutationError,
  });

  const split = useMutation({
    mutationFn: (ids: string[]) => ingestionService.splitEmailed(ids),
    onSuccess: () => { setSelected(new Set()); setError(null); invalidate(); },
    onError: onMutationError,
  });

  const busy = discard.isPending || reconcile.isPending || split.isPending || notADeal.isPending;

  /** The deal of record leads: a converted message if there is exactly one,
   *  otherwise the earliest-sent. Named explicitly in the confirm bar so the
   *  operator never has to guess which record absorbs the others. */
  const reconcilePrimary = useMemo(() => {
    if (selectedRows.length < 2) return null;
    const converted = selectedRows.filter((r) => r.convertedToLoanId);
    if (converted.length === 1) return converted[0];
    if (converted.length > 1) return null; // ambiguous — backend refuses too
    return [...selectedRows].sort(
      (a, b) => new Date(a.receivedAt ?? 0).getTime() - new Date(b.receivedAt ?? 0).getTime(),
    )[0];
  }, [selectedRows]);

  /** Discard is all-or-nothing on the server: one live deal in the selection
   *  refuses the whole batch. Surface that here rather than letting the
   *  operator hit a 409 they then have to decode. */
  const liveDealsInSelection = useMemo(
    () => selectedRows.filter((r) => r.convertedToLoanId),
    [selectedRows],
  );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const allVisibleSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));
  const toggleAll = () =>
    setSelected(allVisibleSelected ? new Set() : new Set(visible.map((r) => r.id)));

  return (
    <div className="panel flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="panel-header flex-shrink-0 gap-3">
        <span className="panel-header-title">Emailed Intake</span>
        <div className="flex items-center gap-1" role="tablist" aria-label="Filter emailed intake">
          {FILTERS.map((f) => {
            const n = f.countKey ? counts[f.countKey] : undefined;
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(f.key)}
                className={`rounded px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider transition-colors ${
                  active
                    ? 'bg-amber-400/[0.12] text-amber-200'
                    : 'text-stone-500 hover:text-stone-300'
                }`}
              >
                {f.label}
                {typeof n === 'number' && <span className="ml-1 text-stone-600">{n}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {focus && (
        <div
          className="flex flex-wrap items-center gap-2 border-b border-amber-400/10 bg-amber-400/[0.03] px-3 py-1.5 text-[11px] text-stone-300"
          data-testid="emailed-focus"
        >
          <span>
            Showing the {focus.ids.length} record{focus.ids.length === 1 ? '' : 's'} behind{' '}
            <span className="text-amber-200">{focus.label}</span>
            {focusNotEmailed > 0 && !isLoading && (
              <span className="text-stone-500">
                {' '}· {focusNotEmailed} not an email — listed on the Ingestion Queue tab
              </span>
            )}
          </span>
          {onClearFocus && (
            <button
              type="button"
              onClick={onClearFocus}
              className="ml-auto text-[11px] text-stone-500 hover:text-stone-300"
            >
              Show all
            </button>
          )}
        </div>
      )}

      {/* Bulk action bar — only present when there is a selection to act on. */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-amber-400/10 bg-amber-400/[0.03] px-3 py-1.5">
          <span className="font-mono text-[10px] uppercase tracking-wider text-amber-200">
            {selected.size} selected
          </span>
          <button
            onClick={() => setConfirming('discard')}
            disabled={busy || liveDealsInSelection.length > 0}
            title={
              liveDealsInSelection.length > 0
                ? `${liveDealsInSelection.length} selected message(s) are already live deals — deselect them, or kill the deal instead.`
                : undefined
            }
            className="flex items-center gap-1 rounded border border-red-400/25 px-2 py-0.5 text-[11px] text-red-300 transition-colors hover:bg-red-400/10 disabled:opacity-40"
          >
            <Trash2 className="h-3 w-3" aria-hidden /> Discard as junk
          </button>
          {liveDealsInSelection.length > 0 && (
            <button
              onClick={() => setConfirming('notADeal')}
              disabled={busy}
              title={
                `${liveDealsInSelection.length} selected message(s) already became deals. ` +
                'Remove both the message and the deal — archived, not killed, so junk ' +
                'never enters the funnel or kill reporting. Reversible.'
              }
              className="flex items-center gap-1 rounded border border-red-400/40 bg-red-400/[0.06] px-2 py-0.5 text-[11px] text-red-200 transition-colors hover:bg-red-400/15 disabled:opacity-40"
            >
              <Trash2 className="h-3 w-3" aria-hidden />
              Remove {liveDealsInSelection.length} from pipeline — not a deal
            </button>
          )}
          <button
            onClick={() => setConfirming('reconcile')}
            disabled={busy || selected.size < 2}
            title={selected.size < 2 ? 'Select two or more messages about the same property' : undefined}
            className="flex items-center gap-1 rounded border border-amber-400/25 px-2 py-0.5 text-[11px] text-amber-200 transition-colors hover:bg-amber-400/10 disabled:opacity-40"
          >
            <Link2 className="h-3 w-3" aria-hidden /> Reconcile into one deal
          </button>
          <button
            onClick={() => split.mutate([...selected])}
            disabled={busy || !selectedRows.some((r) => (r.groupSize ?? 1) > 1)}
            className="flex items-center gap-1 rounded border border-stone-500/30 px-2 py-0.5 text-[11px] text-stone-300 transition-colors hover:bg-stone-400/10 disabled:opacity-40"
          >
            <Unlink className="h-3 w-3" aria-hidden /> Split
          </button>
          <button
            onClick={() => { setSelected(new Set()); setConfirming(null); }}
            className="ml-auto text-[11px] text-stone-500 hover:text-stone-300"
          >
            Clear
          </button>
        </div>
      )}

      {confirming && (
        <div className="border-b border-amber-400/10 bg-black/40 px-3 py-2" role="alertdialog" aria-live="polite">
          {confirming === 'notADeal' ? (
            <p className="text-[11px] text-stone-300">
              Remove {liveDealsInSelection.length} deal
              {liveDealsInSelection.length === 1 ? '' : 's'} from the pipeline and archive the
              message{selected.size === 1 ? '' : 's'} behind{' '}
              {selected.size === 1 ? 'it' : 'them'}?{' '}
              <span className="text-stone-400">
                Archived, not killed — junk was never an opportunity, so it stays out of the
                funnel and out of kill-reason reporting. This is reversible.
              </span>
            </p>
          ) : confirming === 'discard' ? (
            <p className="text-[11px] text-stone-300">
              Archive {selected.size} message{selected.size === 1 ? '' : 's'} out of the pipeline?
              They leave every queue and metric but stay retrievable for audit. Records already
              promoted to a live deal are refused.
            </p>
          ) : (
            <p className="text-[11px] text-stone-300">
              {reconcilePrimary ? (
                <>
                  Join {selected.size} messages into one deal, led by{' '}
                  <span className="text-amber-200">
                    {reconcilePrimary.subject || reconcilePrimary.id}
                  </span>
                  . The others become supporting mail on that deal.
                </>
              ) : (
                <span className="text-amber-300">
                  More than one selected message is already its own live deal — apply them to a
                  target deal instead of reconciling here.
                </span>
              )}
            </p>
          )}
          <div className="mt-1.5 flex items-center gap-2">
            <button
              onClick={() => {
                if (confirming === 'discard') discard.mutate([...selected]);
                else if (confirming === 'notADeal') notADeal.mutate([...selected]);
                else if (reconcilePrimary) {
                  reconcile.mutate({
                    primaryId: reconcilePrimary.id,
                    memberIds: [...selected].filter((id) => id !== reconcilePrimary.id),
                  });
                }
              }}
              disabled={busy || (confirming === 'reconcile' && !reconcilePrimary)}
              className="rounded border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[11px] text-amber-200 disabled:opacity-40"
            >
              {busy ? 'Working…' : 'Confirm'}
            </button>
            <button
              onClick={() => setConfirming(null)}
              className="text-[11px] text-stone-500 hover:text-stone-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <p className="border-b border-red-400/20 bg-red-400/[0.06] px-3 py-1.5 text-[11px] text-red-300" role="alert">
          {error}
        </p>
      )}

      {/* Contained horizontal overflow: the page itself never scrolls sideways. */}
      <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
        {isLoading ? (
          <p className="flex items-center gap-2 px-3 py-6 text-[11px] text-stone-500">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Loading the originations mailbox…
          </p>
        ) : isError ? (
          <div className="px-3 py-6">
            <p className="text-[11px] text-red-300">Could not load emailed intake.</p>
            <button onClick={() => refetch()} className="mt-1 text-[11px] text-amber-300 hover:underline">
              Retry
            </button>
          </div>
        ) : visible.length === 0 ? (
          <p className="px-3 py-6 text-[11px] text-stone-500">
            {rows.length === 0
              ? 'No mail has been ingested from the originations mailbox yet.'
              : 'No messages match this filter.'}
          </p>
        ) : (
          <table className="dense-table">
            {/* sticky must sit on the cells: position on <thead> does not
                apply to <th> in a table layout, so the header scrolled away. */}
            <thead>
              <tr>
                <th scope="col" className="sticky top-0 z-10 w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    aria-label="Select all visible messages"
                    className="h-3 w-3 accent-amber-400"
                  />
                </th>
                <th scope="col" className="sticky top-0 z-10 w-20">Date</th>
                <th scope="col" className="sticky top-0 z-10 w-20" title="New York time">Time (ET)</th>
                <th scope="col" className="sticky top-0 z-10 w-56">From</th>
                <th scope="col" className="sticky top-0 z-10">Subject</th>
                <th scope="col" className="sticky top-0 z-10 w-24">Attachments</th>
                <th scope="col" className="sticky top-0 z-10 w-20">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const { date, time, title } = receivedParts(row.receivedAt);
                const tone = statusTone(row);
                const grouped = (row.groupSize ?? 1) > 1;
                const isDupe = Boolean(row.duplicateOf);
                return (
                  <tr
                    key={row.id}
                    className={`deal-table-row ${selected.has(row.id) ? 'selected' : ''} ${
                      isDupe ? 'opacity-60' : ''
                    }`}
                    onClick={() => onOpenRecord?.(row.id)}
                    style={
                      grouped
                        ? { boxShadow: `inset 3px 0 0 0 ${groupAccent(row.groupId)}` }
                        : undefined
                    }
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(row.id)}
                        onChange={() => toggle(row.id)}
                        aria-label={`Select ${row.subject || row.id}`}
                        className="h-3 w-3 accent-amber-400"
                      />
                    </td>
                    <td className="font-mono text-[11px] text-stone-300" title={title}>{date}</td>
                    <td className="font-mono text-[11px] text-stone-400">{time}</td>
                    <td className="truncate">
                      <span className="text-stone-200">{row.senderName || row.sender || '—'}</span>
                      {row.senderName && row.sender && (
                        <span className="block truncate font-mono text-[10px] text-stone-600">
                          {row.sender}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-stone-200">{row.subject || '(no subject)'}</span>
                        {isDupe && (
                          <span
                            title={
                              row.duplicateReason === 'identical'
                                ? `Identical to ${row.duplicateOf}`
                                : `Held as a re-send of ${row.duplicateOf}`
                            }
                            className="flex flex-shrink-0 items-center gap-0.5 rounded border border-stone-500/40 px-1 font-mono text-[9px] uppercase text-stone-400"
                          >
                            <Copy className="h-2.5 w-2.5" aria-hidden /> Dup
                          </span>
                        )}
                        {row.junkSuspected && !isDupe && (
                          <span
                            title={row.junkReason || 'Suspected junk'}
                            className="flex flex-shrink-0 items-center gap-0.5 rounded border border-amber-400/30 px-1 font-mono text-[9px] uppercase text-amber-300"
                          >
                            <AlertTriangle className="h-2.5 w-2.5" aria-hidden /> Junk?
                          </span>
                        )}
                        {grouped && (
                          <span
                            title={groupChip(row).title}
                            className="flex-shrink-0 rounded border px-1 font-mono text-[9px] uppercase"
                            style={{ color: groupAccent(row.groupId), borderColor: `${groupAccent(row.groupId)}55` }}
                          >
                            {groupChip(row).label}
                          </span>
                        )}
                      </div>
                      {row.propertyName && (
                        <span className="block truncate text-[10px] text-stone-600">{row.propertyName}</span>
                      )}
                      {/* Every reason the record is held out of the pipeline, not only the
                          first written: an email listing several deals with a fragment for
                          a name is held for both (ENT-12). */}
                      {!row.convertedToLoanId && (row.holdReasons?.length ?? 0) > 0 && (
                        <span
                          data-testid="held-reasons"
                          title={`Held: ${row.holdReasons!.join(' · ')}`}
                          className="block truncate text-[10px] text-amber-300/80"
                        >
                          Held: {row.holdReasons!.join(' · ')}
                        </span>
                      )}
                    </td>
                    <td>
                      {row.attachmentCount > 0 ? (
                        <span
                          className="flex items-center gap-1 text-stone-300"
                          title={row.attachments.map((a) => a.filename).filter(Boolean).join('\n')}
                        >
                          <Paperclip className="h-3 w-3 text-amber-300/70" aria-hidden />
                          {row.attachmentCount}
                        </span>
                      ) : (
                        <span className="text-stone-700">—</span>
                      )}
                    </td>
                    <td>
                      <span
                        title={tone.title}
                        className={`rounded border px-1 font-mono text-[9px] uppercase ${tone.className}`}
                      >
                        {tone.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export const ORIGINATION_MAILBOX = 'origination@helios-fintech.com';

export function formatReceivedDisplay(value: string): string {
  const { date, time, title } = receivedParts(value);
  return time ? `${date} · ${time} ET` : date || title;
}

